// setPartitionScheme — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'partition-table', table, spec })` op (DSE2 §3.E1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice E1 — Partitioning UI.
//
// Per-engine surface (mirrors DSE2's `synthesizePartitionTable`):
//   - mysql:  ALTER TABLE t PARTITION BY <spec> — promote a non-partitioned
//             table to partitioned. Accepts RANGE / LIST / HASH / KEY.
//   - oracle: ALTER TABLE t MODIFY PARTITION BY <spec> (12.2+). Accepts
//             RANGE / LIST / HASH / REFERENCE / SYSTEM.
//   - pgsql:  rejects — pgsql sets partitioning at CREATE TABLE time.
//             Tooltip redirects to the parent-with-ATTACH pattern (Slice D1
//             rebuild + add-partition workflow).
//   - mssql:  rejects — mssql binds partitioning via a partition scheme on
//             CREATE TABLE / CREATE INDEX. The mssql scheme/function ops
//             are out of v2 Slice E1 scope.
//   - sqlite: rejects — no partitioning support.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { QualifiedName } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  PartitioningKind,
  PartitioningSpec,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface SetPartitionSchemeArgs {
  readonly table: QualifiedName;
  readonly spec: PartitioningSpec;
}

export function buildSetPartitionSchemeEdit(args: SetPartitionSchemeArgs): DdlEdit {
  return { op: 'partition-table', table: args.table, spec: args.spec };
}

export async function commitSetPartitionScheme(
  ctx: VqeDdlEditContext,
  args: SetPartitionSchemeArgs,
): Promise<void> {
  const edit = buildSetPartitionSchemeEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const PARTITION_TABLE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mysql', 'oracle',
] as const);

export function isSetPartitionSchemeSupported(engine: SqlEngine): boolean {
  return PARTITION_TABLE_SUPPORTED.has(engine);
}

export function getSetPartitionSchemeUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'postgres') {
    return (
      'PostgreSQL declares partitioning at CREATE TABLE time. Recreate the '
      + 'table with PARTITION BY <strategy> as the parent, then add child '
      + 'partitions via ALTER TABLE … ATTACH PARTITION (the [+ Partition] '
      + 'affordance).'
    );
  }
  if (engine === 'mssql') {
    return (
      'SQL Server binds partitioning through a partition scheme + function on '
      + 'CREATE TABLE / CREATE INDEX. Create the partition function and '
      + 'scheme separately and rebuild the table on the scheme.'
    );
  }
  if (engine === 'sqlite') {
    return (
      'SQLite has no partitioning support. Logical partitioning typically uses '
      + 'multiple databases attached via ATTACH DATABASE, with views unioning '
      + 'across them.'
    );
  }
  return '';
}

/** Engine-supported partitioning kinds. Mirrors DSE2's `formatPartitioningSpec`
 *  reject paths so the form can hide unsupported strategies pre-flight. */
const ENGINE_PARTITION_KINDS: Record<SqlEngine, readonly PartitioningKind[]> = {
  mssql: [],
  postgres: [],
  mysql: ['range', 'list', 'hash', 'key'],
  oracle: ['range', 'list', 'hash', 'reference', 'system'],
  sqlite: [],
};

export function getSupportedPartitionKinds(engine: SqlEngine): readonly PartitioningKind[] {
  return ENGINE_PARTITION_KINDS[engine];
}

export function isPartitionKindSupported(
  engine: SqlEngine,
  kind: PartitioningKind,
): boolean {
  return ENGINE_PARTITION_KINDS[engine].includes(kind);
}

/** Pre-flight validator. Returns null when args are acceptable, else a
 *  user-facing message. */
export function validateSetPartitionSchemeForEngine(
  engine: SqlEngine,
  args: SetPartitionSchemeArgs,
): string | null {
  if (!isSetPartitionSchemeSupported(engine)) {
    return getSetPartitionSchemeUnsupportedTooltip(engine);
  }
  const spec = args.spec;
  if (!isPartitionKindSupported(engine, spec.kind)) {
    const supported = ENGINE_PARTITION_KINDS[engine].join(', ');
    return (
      `${engine} does not support PARTITION BY ${spec.kind.toUpperCase()}. `
      + `Supported strategies on ${engine}: ${supported}.`
    );
  }
  if (spec.kind !== 'reference' && spec.kind !== 'system') {
    if (spec.columns.length === 0) {
      return 'Partitioning requires at least one column.';
    }
  }
  if (spec.kind === 'reference' && spec.refConstraint.trim().length === 0) {
    return 'REFERENCE partitioning requires a parent foreign-key constraint name.';
  }
  return null;
}
